import { zodResolver } from "@hookform/resolvers/zod";
import { LucideLoaderCircle } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { FiDownload } from "react-icons/fi";
import { z } from "zod";

import { Clipboard } from "@/components/Clipboard";
import { LazyImage } from "@/components/LazyImage";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

async function toDataURL(url: string) {
  const blob = await fetch(url).then((res) => res.blob());
  return URL.createObjectURL(blob);
}
const Shorten = ({ domains }: { domains: string[]; dark?: boolean }) => {
  const [loading, setLoading] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [generatedLink, setGeneratedLink] = useState<any | undefined>(
    undefined,
  );

  const clearGeneratedLink = () => setGeneratedLink(undefined);

  const formSchema = z.object({
    longUrl: z.string().url({
      message: "Enter a valid URL",
    }),
    domain: z.string(),
    backHalf: z.string().min(0).max(8),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      longUrl: "",
      domain: domains[0],
      backHalf: "",
    },
  });
  // 2. Define a submit handler.
  function onSubmit(values: z.infer<typeof formSchema>) {
    // Do something with the form values.
    // ✅ This will be type-safe and validated.

    fetch("https://" + import.meta.env.PUBLIC_API_URL + "/tools/shortener", {
      method: "post",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        link: values.longUrl,
        id: values.backHalf ? "/" + values.backHalf : undefined,
        domain: values.domain,
      }),
    })
      .then((res) => res.json())
      .then((link) => {
        setGeneratedLink(link);
        setLoading(false);
        if (link.url) form.reset();
        else
          form.setError("backHalf", {
            message: "This Back-Half is already taken",
          });
      });

    setLoading(true);
  }
  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="dark:text-foreground w-full">
            <h2 className="text-4xl font-semibold">Shorten a Long Link</h2>
            <div className="mt-10">
              <FormField
                control={form.control}
                name="longUrl"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="md:text-lg">
                      Paste a long URL
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Paste your Loooong URL here"
                        className="bg-background dark:bg-foreground"
                        {...field}
                      />
                    </FormControl>
                    {/* <FormDescription>
                  This is your public display name.
                </FormDescription> */}
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="mt-3 flex w-full gap-2 md:w-[60%]">
                <FormField
                  control={form.control}
                  name={`domain`}
                  defaultValue={domains[0]}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="md:text-lg">Domain</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger
                            className="bg-background dark:bg-foreground text-slate-900"
                            aria-label="Choose a domain"
                          >
                            <SelectValue placeholder="Select a type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {domains.map((opt) => (
                            <SelectItem value={opt} key={`domain-${opt}`}>
                              {opt}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <span className={"mt-9 text-3xl font-semibold"}>/</span>

                <FormField
                  control={form.control}
                  name="backHalf"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="md:text-lg">
                        Custom Back-Half
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="my-cool-link"
                          className="bg-background dark:bg-foreground"
                          {...field}
                        />
                      </FormControl>
                      {/* <FormDescription>
                  This is your public display name.
                </FormDescription> */}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              {loading ? (
                <Button
                  className="bg-primary/75 mt-5 cursor-not-allowed"
                  type="submit"
                >
                  <LucideLoaderCircle className="mr-2 h-5 w-5 animate-spin" />
                  Get your link
                </Button>
              ) : (
                <Button className="mt-5" type="submit">
                  Get your link
                </Button>
              )}
            </div>
          </div>
        </form>
      </Form>
      <Dialog
        open={!!generatedLink && generatedLink.url}
        onOpenChange={clearGeneratedLink}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Congratulations! Your link is ready to share.
            </DialogTitle>
            <DialogDescription>
              <div className="mt-12">
                <p className="text-muted-foreground text-lg">
                  Here's your unique link:
                </p>
                <div className="mt-3 flex items-center gap-4">
                  <Input
                    className="h-10"
                    value={generatedLink?.url}
                    onChange={() => null}
                    placeholder="Your link"
                  />
                  <Clipboard
                    content={generatedLink?.url}
                    className="text-primary size-7"
                  />
                </div>
                <p className="text-muted-foreground mt-10 text-lg">
                  Here's your QR code:
                </p>
                <div className="mt-3 flex gap-4">
                  <div className="rounded-lg border-2 p-4">
                    <LazyImage
                      src={
                        "https://img.smartgraphics.mk/qr?w=150&h=150&value=" +
                        encodeURIComponent(generatedLink?.url)
                      }
                      height={150}
                      width={150}
                    />
                  </div>

                  <Button
                    variant={"ghost"}
                    onClick={async () => {
                      const a = document.createElement("a");
                      a.href = await toDataURL(
                        "https://img.smartgraphics.mk/qr?value=" +
                          encodeURIComponent(generatedLink?.url),
                      );
                      a.download = "qr.png";
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                    }}
                    size={"icon"}
                    className="size-7"
                  >
                    <FiDownload className="text-primary h-full w-full" />
                  </Button>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <Button onClick={() => clearGeneratedLink()}>
            Create a new link
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export { Shorten };
